import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Copy, Check, Sparkles } from "lucide-react";

// One-shot dismissible welcome banner shown to a customer after they create
// an account. Reveals the FIRST10 welcome code with a Copy button.
//
// State machine (localStorage key `luviarx_welcome_first10_v1`):
//   "show"      → banner visible on every page load until dismissed
//   "dismissed" → banner never renders again
//   null        → banner never renders (user hasn't just registered)
//
// The Register page sets the key to "show" on successful sign-up. Because the
// banner mounts at app root (before registration happens), we re-read the
// flag on every route change so the post-register redirect surfaces it.
// Dismissing (× button) flips it to "dismissed" so it doesn't come back.

const STORAGE_KEY = "luviarx_welcome_first10_v1";
const CODE = "FIRST10";

export const WelcomeOfferBanner = () => {
  const location = useLocation();
  const [state, setState] = useState("hidden");   // "hidden" | "show" | "dismissed"
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === "show") setState("show");
    else if (v === "dismissed") setState("dismissed");
    else setState("hidden");
  }, [location.pathname]);

  const dismiss = () => {
    setState("dismissed");
    try { window.localStorage.setItem(STORAGE_KEY, "dismissed"); } catch { /* ignore */ }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard?.writeText(CODE);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore clipboard failures */ }
  };

  if (state !== "show") return null;

  return (
    <section
      data-testid="welcome-offer-banner"
      className="container-x pt-4 md:pt-6"
    >
      <div
        className="relative overflow-hidden rounded-2xl border shadow-sm"
        style={{
          backgroundColor: "#F5EAE6",
          borderColor: "rgba(139, 34, 82, 0.25)",
        }}
      >
        <button
          type="button"
          onClick={dismiss}
          data-testid="welcome-offer-banner-dismiss"
          aria-label="Dismiss welcome offer"
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/70 hover:bg-white text-charcoal-500 hover:text-charcoal-700 flex items-center justify-center transition-colors"
        >
          <X className="w-4 h-4" strokeWidth={2} />
        </button>

        <div className="px-5 sm:px-8 py-5 md:py-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6 pr-12">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
            style={{
              backgroundColor: "rgba(139, 34, 82, 0.12)",
              color: "#8B2252",
            }}
          >
            <Sparkles className="w-5 h-5" strokeWidth={2} />
          </div>

          <div className="flex-1 min-w-0">
            <p
              data-testid="welcome-offer-banner-title"
              className="font-display font-bold text-lg md:text-xl leading-snug"
              style={{ color: "#1C1618" }}
            >
              Welcome to LuviaRx!
            </p>
            <p className="mt-1 text-sm text-charcoal-500 leading-relaxed">
              Use code{" "}
              <span
                data-testid="welcome-offer-banner-code"
                className="inline-block font-mono font-bold tracking-[0.14em] px-2 py-0.5 rounded"
                style={{
                  color: "#8B2252",
                  backgroundColor: "rgba(139, 34, 82, 0.1)",
                }}
              >
                {CODE}
              </span>{" "}
              at checkout for <strong style={{ color: "#8B2252" }}>10% off</strong> your first order.
            </p>
          </div>

          <button
            type="button"
            onClick={copyCode}
            data-testid="welcome-offer-banner-copy"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold text-white transition-colors shrink-0 self-start md:self-auto"
            style={{
              backgroundColor: copied ? "#3f7c59" : "#8B2252",
            }}
          >
            {copied ? (
              <>
                <Check className="w-4 h-4" strokeWidth={2.4} />
                Copied
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" strokeWidth={2} />
                Copy Code
              </>
            )}
          </button>
        </div>
      </div>
    </section>
  );
};

export default WelcomeOfferBanner;
